import { ReactNode, useEffect, useState } from 'react';
import Container from '@mui/system/Container';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { AppBar } from '@/components';
import { useAtom, useAtomValue } from 'jotai';
import { demoModeAtom, videoPanelOpenAtom } from '@/store';
import { SurveyVideo } from '@/components/common/SurveyVideo';
import { fetchVideoCredentials } from '@/api/fetchVideoCredentials';
import { useSurvey } from '@/hooks';

const HEADER_HEIGHT = 64;
const XS_HEADER_HEIGHT = 56;

interface VideoCredentials {
  apiKey: string;
  sessionId: string;
  token: string;
}

function VideoPanel() {
  const { survey } = useSurvey();
  const isDemoMode = useAtomValue(demoModeAtom);
  const [credentials, setCredentials] = useState<
    VideoCredentials | undefined
  >();
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    setCredentials(undefined);
    setError(undefined);
    const getCredentials = async () => {
      if (isDemoMode) {
        setCredentials({ apiKey: 'mock', sessionId: 'mock', token: 'mock' });
        return;
      }
      const response = await fetchVideoCredentials(survey.serviceId);
      if (response) {
        setCredentials(response);
      } else {
        setError('Unable to fetch video credentials');
      }
    };
    getCredentials();
  }, [survey.serviceId, isDemoMode]);

  if (!credentials) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        {error ?? 'Loading...'}
      </Box>
    );
  }

  return (
    <SurveyVideo
      apiKey={credentials.apiKey}
      sessionId={credentials.sessionId}
      token={credentials.token}
      demoMode={isDemoMode}
    />
  );
}

function AppLayoutInner({ page }: { page: ReactNode }) {
  const [videoPanelOpen, setVideoPanelOpen] = useAtom(videoPanelOpenAtom);

  return (
    <>
      <AppBar />
      <Box
        sx={{
          display: 'flex',
          height: {
            xs: `calc(100vh - ${XS_HEADER_HEIGHT}px)`,
            sm: `calc(100vh - ${HEADER_HEIGHT}px)`,
          },
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            flex: 1,
            minWidth: 0,
            height: '100%',
            px: { xs: 0, md: 2 },
            py: { xs: 0, md: 3 },
            gap: 2,
          }}
        >
          {page}
        </Container>

        {videoPanelOpen && (
          <Box
            sx={{
              width: { xs: '100%', md: 380 },
              flexShrink: 0,
              height: '100%',
              borderLeft: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'black',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => setVideoPanelOpen(false)}
                sx={{ color: 'white' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <VideoPanel />
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
}

export function AppLayout(page: ReactNode) {
  return <AppLayoutInner page={page} />;
}
